/**
 * Composable for managing recent search history.
 *
 * @returns {Object} Recent searches management utilities.
 * @property {Ref<string[]>} cookiesRecentSearches - Reactive reference to recent searches stored in cookies.
 * @property {Function} saveRecentSearch - Method to save a new recent search.
 * @property {Function} deleteRecentSearch - Method to delete a specific recent search.
 *
 * @example
 * const { cookiesRecentSearches, saveRecentSearch } = useRecentSearches()
 * saveRecentSearch('laptop')
 */
export const useRecentSearches = () => {
  /**
   * Cookie storage for recent searches with a 12-hour expiration.
   *
   * @type {Ref<string[]>}
   * @remarks
   * Stores up to 3 most recent unique search terms.
   */
  const cookiesRecentSearches = useCookie<string[]>('recent-searches', {
    default: () => [],
    maxAge: 43200, // 12 hours in seconds
  })

  /**
   * Checks if a search value already exists in recent searches.
   *
   * @param {string} searchValue - The search term to check.
   * @returns {boolean} Whether the search term is already in recent searches.
   *
   * @example
   * const exists = isSearchValueInRecentSearches('laptop')
   */
  const isSearchValueInRecentSearches = (searchValue: string) => {
    return cookiesRecentSearches.value.includes(searchValue)
  }

  /**
   * Saves a new recent search, maintaining a maximum of 3 unique entries.
   *
   * @param {string} searchValue - The search term to save.
   *
   * @remarks
   * - Prevents duplicate entries
   * - Limits recent searches to 3 items
   * - New searches are added to the beginning of the list
   *
   * @example
   * saveRecentSearch('smartphone')
   */
  const saveRecentSearch = (searchValue: string) => {
    if (isSearchValueInRecentSearches(searchValue)) return
    if (cookiesRecentSearches.value.length >= 3)
      cookiesRecentSearches.value.pop()

    cookiesRecentSearches.value.unshift(searchValue)
  }

  /**
   * Deletes a recent search by its index.
   *
   * @param {number} index - The index of the search term to delete.
   *
   * @example
   * deleteRecentSearch(1) // Removes the second recent search
   */
  const deleteRecentSearch = (index: number) => {
    if (cookiesRecentSearches.value)
      cookiesRecentSearches.value.splice(index, 1)
  }

  return {
    cookiesRecentSearches,
    saveRecentSearch,
    deleteRecentSearch,
  }
}
